import axios from 'axios';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { type InitiateSSOResponse } from '../../api/initiate-sso/InitiateSSOResponse';

export interface UseSSO {
    isSSOEnabled: boolean;
    isCheckingSSO: boolean;
    isRedirectingToSSO: boolean;
    ssoError: string | null;
    checkSSODomain: (email: string) => void;
    initiateSSOLogin: (email: string) => Promise<void>;
    disableSSO: () => void;
}

export function useSSO(ssoDomainsArray: string[]): UseSSO {
    const [isSSOEnabled, setIsSSOEnabled] = useState(false);
    const [isCheckingSSO, setIsCheckingSSO] = useState(false);
    const [isRedirectingToSSO, setIsRedirectingToSSO] = useState(false);
    const [ssoError, setSsoError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Convert array to Set for efficient lookups, memoized to prevent recreation
    const ssoDomains = useMemo(() => new Set(ssoDomainsArray.map((d) => d.toLowerCase())), [ssoDomainsArray]);

    const checkSSODomain = useCallback(
        (email: string): void => {
            const domain = email.split('@')[1]?.toLowerCase();
            if (!domain) {
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            setIsCheckingSSO(true);
            try {
                // Check against cached SSO domains
                const isSSO = ssoDomains.has(domain);
                if (!abortController.signal.aborted) {
                    setIsSSOEnabled(isSSO);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setIsCheckingSSO(false);
                }
            }
        },
        [ssoDomains],
    );

    const initiateSSOLogin = useCallback(async (email: string): Promise<void> => {
        setIsRedirectingToSSO(true);
        setSsoError(null);

        try {
            // Preserve user's intended destination by passing through the redirect query parameter
            let redirectUrl = `${window.location.origin}/login/sso-callback`;
            const params = new URLSearchParams(window.location.search);
            const intendedRedirect = params.get('redirectAfterLogin');
            if (intendedRedirect) {
                // Encode the redirect parameter to avoid issues with special characters
                redirectUrl += `?redirect=${encodeURIComponent(intendedRedirect)}`;
            }

            const response = await axios.post<InitiateSSOResponse>('/api/initiate-sso', {
                email,
                redirectUrl,
            });

            if (response.data.authUrl) {
                window.location.href = response.data.authUrl;
            } else {
                setSsoError('Failed to get authorization URL. Please try again.');
                setIsRedirectingToSSO(false);
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.data && typeof error.response.data === 'object') {
                const errorData = error.response.data as { error?: string };
                setSsoError(errorData.error ?? 'Failed to initiate SSO login. Please try again.');
            } else {
                setSsoError('Failed to initiate SSO login. Please try again.');
            }
            setIsRedirectingToSSO(false);
        }
    }, []);

    const disableSSO = useCallback((): void => {
        setIsSSOEnabled(false);
        setSsoError(null);
        setIsRedirectingToSSO(false);
    }, []);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        isSSOEnabled,
        isCheckingSSO,
        isRedirectingToSSO,
        ssoError,
        checkSSODomain,
        initiateSSOLogin,
        disableSSO,
    };
}
